import { Injectable } from '@angular/core';
import { HttpHeaders } from '@angular/common/http';
import { environment } from '~env';
import { Pageable } from '~shared/models/pageable.model';
import { Contact } from '~shared/models/data/contact.model';
import { Observable, map } from 'rxjs';
import { RecordSearchParams } from '~shared/models/data/record-search.model';
import { BaseApiService } from '../base.api-service';

@Injectable({
  providedIn: 'root',
})
export class EntityApiService extends BaseApiService {
  /**
   * `search` / `search_fields` voyagent À CÔTÉ de `filter`, jamais dedans : le
   * serveur compose le nœud de recherche et le combine en ET avec le filtre
   * reçu. Une recherche n'écrase donc plus les conditions déjà posées.
   */
  find(
    data: {
      module: string;
      table: string;
      limit: number;
      offset: number;
      orderBy: string | undefined;
      order: 'asc' | 'desc' | undefined;
      filters: readonly unknown[];
      relation?: string;
    } & RecordSearchParams
  ): Observable<Pageable<any>> {
    return this.http
      .get<Pageable<any>>(
        environment.apiBasePaths.data + `/entity/${data.module}/${data.table}`,
        {
          params: this.buildParams({
            limit: data.limit,
            offset: data.offset,
            orderBy: data.orderBy,
            order: data.order,
            filter: data.filters,
            relation: data.relation,
            search: data.search,
            search_fields: data.search_fields,
          }),
        }
      )
      .pipe(
        map((response) => {
          return response;
        })
      );
  }

  findById(module: string, table: string, id: number) {
    return this.http.get<Contact>(
      environment.apiBasePaths.data + `/entity/${module}/${table}/${id}`,
      { params: this.buildParams() }
    );
  }

  create(module: string, table: string, datas: FormData) {
    return this.http.post(
      environment.apiBasePaths.data + `/entity/${module}/${table}`,
      datas,
      {
        params: this.buildParams(),
        headers: this.formDataHeaders(),
      }
    );
  }

  update(module: string, table: string, id: number, datas: URLSearchParams) {
    return this.http.patch(
      environment.apiBasePaths.data + `/entity/${module}/${table}/${id}`,
      datas,
      {
        params: this.buildParams(),
        // Sans ce header, Angular ne sérialise pas un URLSearchParams comme du
        // form-urlencoded → le back ne retrouve pas `datas` (400 datas manquant).
        headers: new HttpHeaders({
          'content-type': 'application/x-www-form-urlencoded',
        }),
      }
    );
  }

  /**
   * Fusion N→1 : `idsToDelete` sont absorbés dans `idToUpdate` (relations
   * transférées puis suppression), et `values` (technames → valeur) est
   * appliqué au survivant en PATCH — un champ absent garde sa valeur.
   */
  merge(
    module: string,
    table: string,
    idToUpdate: number,
    idsToDelete: number[],
    values: Record<string, unknown>
  ) {
    const formData = new URLSearchParams();
    formData.append(
      'datas',
      JSON.stringify({ delete_ids: idsToDelete, values })
    );
    return this.http.patch(
      environment.apiBasePaths.data +
        `/entity/${module}/${table}/merge/${idToUpdate}`,
      formData,
      {
        params: this.buildParams(),
        headers: new HttpHeaders({
          'content-type': 'application/x-www-form-urlencoded',
        }),
      }
    );
  }

  updateByEndpoint(endpoint: string, datas: URLSearchParams) {
    return this.http.patch(environment.apiBasePaths.data + endpoint, datas, {
      params: this.buildParams(),
      headers: new HttpHeaders({
        'content-type': 'application/x-www-form-urlencoded',
      }),
    });
  }

  deleteOne(module: string, table: string, id: number) {
    return this.http.delete(
      environment.apiBasePaths.data + `/entity/${module}/${table}/${id}`,
      { params: this.buildParams() }
    );
  }

  delete(
    module: string,
    table: string,
    filters: readonly unknown[],
    force = false
  ) {
    return this.http.delete(
      environment.apiBasePaths.data + `/entity/${module}/${table}`,
      {
        params: this.buildParams({
          filter: filters,
          force: force ? 'true' : undefined,
        }),
      }
    );
  }

  getLinkedRecords(
    moduleSlug: string,
    tableSlug: string,
    id: number,
    targetModuleSlug: string,
    targetTableSlug: string,
    fieldId: number,
    limit = 10,
    offset = 0,
    orderBy = 'id',
    order: 'asc' | 'desc' = 'desc',
    filters?: readonly unknown[],
    search: RecordSearchParams = {}
  ) {
    return this.http.get<any>(
      environment.apiBasePaths.data +
        `/entity/${moduleSlug}/${tableSlug}/${id}/${targetModuleSlug}/${targetTableSlug}/${fieldId}`,
      {
        params: this.buildParams({
          limit,
          offset,
          order_by: orderBy,
          order,
          // Même contrat que `find` : filtres de recherche transmis au back,
          // omis si absents (buildParams saute les valeurs undefined).
          filter: filters?.length ? filters : undefined,
          // La recherche porte sur la table CIBLE du lien : c'est elle que le
          // serveur interroge, restreinte aux ids liés.
          search: search.search,
          search_fields: search.search_fields,
        }),
      }
    );
  }

  unlinkRecord(unlinkEndpoint: string) {
    return this.http.delete(environment.apiBasePaths.data + unlinkEndpoint, {
      params: this.buildParams(),
    });
  }

  linkRecords(endpoint: string, datas: FormData) {
    return this.http.post(environment.apiBasePaths.data + endpoint, datas, {
      params: this.buildParams(),
      headers: this.formDataHeaders(),
    });
  }

  sendMail(datas: FormData) {
    return this.http.post(
      environment.apiBasePaths.data + `/email/send`,
      datas,
      { headers: this.formDataHeaders() }
    );
  }

  addEmail(idContact: number, datas: FormData) {
    return this.http.post(
      environment.apiBasePaths.data + `/contact/${idContact}/email`,
      datas,
      {
        headers: new HttpHeaders({
          'content-type': 'application/x-www-form-urlencoded',
        }),
      }
    );
  }

  addPhone(idContact: number, datas: FormData) {
    return this.http.post(
      environment.apiBasePaths.data + `/contact/${idContact}/phone`,
      datas,
      {
        headers: new HttpHeaders({
          'content-type': 'application/x-www-form-urlencoded',
        }),
      }
    );
  }
}
